import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DetailHeader from '../components/DetailHeader';
import DetailControls from '../components/DetailControls';
import AppInsightsSummaryStrip from '../components/AppInsightsSummaryStrip';
import CorrelationTracePanel from '../components/CorrelationTracePanel';
import RecentTracesPanel from '../components/RecentTracesPanel';
import type { Env } from '../types';

export default function EnvAppInsightsDetail({ env }: { env: Env }) {
  const [windowHours, setWindowHours] = useState(24);
  const [refreshToken, setRefreshToken] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCorrelationId = searchParams.get('correlationId') ?? '';
  const [activeCorrelationId, setActiveCorrelationId] = useState(initialCorrelationId);

  // Keep URL in sync when user picks an ID from RecentTracesPanel — so the
  // current trace is shareable / bookmarkable.
  useEffect(() => {
    if (activeCorrelationId && activeCorrelationId !== searchParams.get('correlationId')) {
      const next = new URLSearchParams(searchParams);
      next.set('correlationId', activeCorrelationId);
      setSearchParams(next, { replace: true });
    }
  }, [activeCorrelationId]);

  return (
    <div className="page">
      <DetailHeader
        env={env}
        title="Application Insights"
        subtitle="Pick a recent correlation ID from the list, or paste one. The trace panel below loads the full journey — sent/received data for every action, grouped by workflow."
      />
      <DetailControls
        windowHours={windowHours}
        onWindowChange={setWindowHours}
        onRefresh={() => setRefreshToken((t) => t + 1)}
      />
      <AppInsightsSummaryStrip env={env} windowHours={windowHours} refreshToken={refreshToken} />
      <section className="tile-grid tile-grid-wide">
        <RecentTracesPanel
          env={env}
          onSelect={setActiveCorrelationId}
          selectedCorrelationId={activeCorrelationId || undefined}
        />
        <CorrelationTracePanel
          env={env}
          initialCorrelationId={activeCorrelationId}
          refreshToken={refreshToken}
        />
      </section>
    </div>
  );
}
